import * as React from 'react';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import { tabsClasses } from '@mui/material/Tabs';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SideMenuMobile from './SideMenuMobile';
import MenuButton from './MenuButton';
import DynamicsLogo from '../../../../components/images/DynamicsLogo';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useLocation } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const Toolbar = styled(MuiToolbar)({
  width: '100%',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',
  gap: '12px',
  flexShrink: 0,
  [`& ${tabsClasses.flexContainer}`]: {
    gap: '8px',
    p: '8px',
    pb: 0,
  },
});

export default function AppNavbar() {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(0); // For Bottom Navigation
  const navigate = useNavigate(); // For navigation
  const location = useLocation(); // To track the current route

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  // Function to handle bottom navigation change and route change
  const handleBottomNavChange = (event, newValue) => {
    setValue(newValue);

    // Handling navigation on Bottom Navigation change
    switch (newValue) {
      case 0:
        navigate('/home'); // Navigate to Home route
        break;
      case 1:
        navigate('/recruit-agent'); // Navigate to Recruit Agent route
        break;
      case 2:
        navigate('/transaction'); // Navigate to Transaction route
        break;
      case 3:
        navigate('/profile'); // Navigate to Profile route
        break;
      default:
        break;
    }
  };

  // Set the BottomNavigation value based on the current route
  React.useEffect(() => {
    switch (location.pathname) {
      case '/home':
        setValue(0);
        break;
      case '/recruit-agent':
        setValue(1);
        break;
      case '/transaction':
        setValue(2);
        break;
      case '/profile':
        setValue(3);
        break;
      default:
        setValue(0); // Default to Home if no route matches
        break;
    }
  }, [location.pathname]);

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'auto', md: 'none' }, // Hide on desktop, show on mobile
          boxShadow: 0,
          bgcolor: '#ffffff', // Set transparent background on mobile
          backgroundImage: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
          top: 'var(--template-frame-height, 0px)',
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensure the AppBar is on top
        }}
      >
        <Toolbar variant="regular">
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              flexGrow: 1,
              width: '100%',
              gap: 1,
              justifyContent: 'space-between', // Ensure elements are spaced correctly
            }}
          >
            {/* Centered DynamicsLogo */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: 'center', // Centers the logo
                flexGrow: 1, // Ensures the logo takes up remaining space to center
              }}
            >
              <DynamicsLogo height="30px" />
            </Stack>

            {/* Menu Button aligned to the right */}
            <MenuButton aria-label="menu" onClick={toggleDrawer(true)} sx={{ ml: 'auto' }}>
              <MenuRoundedIcon />
            </MenuButton>

            {/* Side Menu for Mobile */}
            <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Bottom Navigation visible only on mobile */}
      <BottomNavigation
        value={value}
        onChange={handleBottomNavChange}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          width: '100%',
          bgcolor: '#ffffff', // Set transparent background on mobile
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: 2,
          display: { xs: 'flex', md: 'none' }, // Show on mobile only
          zIndex: (theme) => theme.zIndex.drawer + 2, // Ensure it is on top of other elements
        }}
      >
        <BottomNavigationAction
          label="Home"
          icon={<HomeIcon />}
          onClick={() => navigate('/home')}
          sx={{
            color: value === 0 ? 'primary.main' : 'text.secondary',
          }}
        />
        <BottomNavigationAction
          label="Rekrut Agen"
          icon={<PersonAddIcon />}
          onClick={() => navigate('/recruit-agent')}
          sx={{
            color: value === 1 ? 'primary.main' : 'text.secondary',
          }}
        />
        <BottomNavigationAction
          label="Transaksi"
          icon={<PaymentIcon />}
          onClick={() => navigate('/transaction')}
          sx={{
            color: value === 2 ? 'primary.main' : 'text.secondary', // Corrected the index to 2
          }}
        />
        <BottomNavigationAction
          label="Profile"
          icon={<AccountCircleIcon />}
          onClick={() => navigate('/profile')}
          sx={{
            color: value === 3 ? 'primary.main' : 'text.secondary', // Corrected the index to 3
          }}
        />
      </BottomNavigation>
    </Box>
  );
}

export function CustomIcon() {
  return (
    <Box
      sx={{
        width: '1.5rem',
        height: '1.5rem',
        bgcolor: 'black',
        borderRadius: '999px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'center',
        backgroundImage:
          'linear-gradient(135deg, hsl(210, 98%, 60%) 0%, hsl(210, 100%, 35%) 100%)',
        color: 'hsla(210, 100%, 95%, 0.9)',
        border: '1px solid',
        borderColor: 'hsl(210, 100%, 55%)',
        boxShadow: 'inset 0 2px 5px rgba(255, 255, 255, 0.3)',
      }}
    >
      <DashboardRoundedIcon color="inherit" sx={{ fontSize: '1rem' }} />
    </Box>
  );
}
